import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Heart, Calendar, ArrowLeft, Newspaper } from "lucide-react";
import { api } from "../lib/api";
import { formatDateDisplay } from "../lib/utils";
import { toast } from "sonner";
import Loading from "../components/Loading";

const TAG_STYLES = {
  news:       "bg-sky-500/20 text-sky-300 border-sky-500/40",
  release:    "bg-violet-500/20 text-violet-300 border-violet-500/40",
  sale:       "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  discussion: "bg-gold/20 text-gold border-gold/40",
};
const TAG_LABELS = { news: "News", release: "Release", sale: "Sale / Deal", discussion: "Discussion" };

// Normalise raw AI output so headings/bullets always start on their own line
function normaliseMarkdown(md) {
  return md
    // ensure any heading (# ## ###) is preceded by a blank line
    .replace(/([^\n])\n?(#{1,3} )/g, "$1\n\n$2")
    // ensure a heading line is followed by a newline (not more text on same line after the heading text)
    // split "## Heading text **bold** more text..." — headings end at first sentence break or \n
    // ensure bullet items (- at line start after optional whitespace) are preceded by a newline
    .replace(/([^\n])(\n[ \t]*- )/g, "$1\n$2")
    // lone # or ## with no space (stray hash) — remove
    .replace(/^#+\s*$/gm, "")
    // collapse 3+ blank lines to 2
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Strip **bold** markers from plain text (for headings where we don't want bold inside h2)
function stripBold(text) {
  return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1");
}

// Lightweight markdown renderer — handles # ## ### headings, **bold**, bullet lists, paragraphs
function renderMarkdown(raw) {
  const md = normaliseMarkdown(raw);
  const blocks = [];
  const lines = md.split("\n");
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul key={key++} className="list-none space-y-2 my-5">
        {listItems.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-slate-300 leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (/^### (.+)/.test(line)) {
      flushList();
      blocks.push(
        <h3 key={key++} className="text-lg font-bold text-gold/90 mt-7 mb-3">{stripBold(line.replace(/^### /, ""))}</h3>
      );
    } else if (/^## (.+)/.test(line)) {
      flushList();
      blocks.push(
        <h2 key={key++} className="text-xl sm:text-2xl font-black tracking-tight text-white mt-10 mb-4 pb-2 border-b border-gold/20">
          {stripBold(line.replace(/^## /, ""))}
        </h2>
      );
    } else if (/^# (.+)/.test(line)) {
      flushList();
      blocks.push(
        <h2 key={key++} className="text-xl sm:text-2xl font-black tracking-tight text-white mt-10 mb-4 pb-2 border-b border-gold/20">
          {stripBold(line.replace(/^# /, ""))}
        </h2>
      );
    } else if (/^- (.+)/.test(line)) {
      listItems.push(line.replace(/^- /, ""));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      // Standalone CTA link — entire line is a single [text](url)
      const ctaMatch = line.match(/^\[(.+?)\]\((https?:\/\/[^\s)]+)\)$/);
      if (ctaMatch) {
        const [, label, url] = ctaMatch;
        const isPreorder = /pre.?order/i.test(label);
        const isSale = /deal|sale|shop|view/i.test(label);
        blocks.push(
          <a
            key={key++}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center justify-between gap-4 mb-8 px-6 py-5 rounded-2xl border-2 font-bold transition-all hover:scale-[1.01] no-underline ${
              isPreorder
                ? "bg-violet-500/20 border-violet-400/50 hover:bg-violet-500/30 hover:border-violet-400 text-violet-200"
                : isSale
                ? "bg-emerald-500/20 border-emerald-400/50 hover:bg-emerald-500/30 hover:border-emerald-400 text-emerald-200"
                : "bg-gold/10 border-gold/40 hover:bg-gold/20 hover:border-gold text-gold"
            }`}
          >
            <span className="text-lg tracking-tight">{label}</span>
            <span className="text-sm uppercase tracking-wider opacity-70 shrink-0">→</span>
          </a>
        );
      } else {
        blocks.push(
          <p key={key++} className="text-slate-300 leading-relaxed text-base mb-4"
            dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
          />
        );
      }
    }
  }
  flushList();
  return blocks;
}

function inlineFormat(text) {
  return text
    .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, "<a href=\"$2\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-gold underline underline-offset-2 hover:text-gold-light\">$1</a>")
    .replace(/\*\*(.+?)\*\*/g, "<strong class=\"text-white font-bold\">$1</strong>")
    .replace(/\*(.+?)\*/g, "<em class=\"text-slate-200 italic\">$1</em>");
}

export default function PostDetailPage() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    const likedIds = JSON.parse(localStorage.getItem("gt_liked_posts") || "[]");
    setLiked(likedIds.includes(id));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    api.get(`/posts/${id}`)
      .then((r) => setPost(r.data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [id]);

  const handleLike = async () => {
    if (liked) return;
    try {
      const r = await api.post(`/posts/${id}/like`);
      setPost(r.data);
      const likedIds = JSON.parse(localStorage.getItem("gt_liked_posts") || "[]");
      localStorage.setItem("gt_liked_posts", JSON.stringify([...likedIds, id]));
      setLiked(true);
    } catch {
      toast.error("Could not register like");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loading variant="block" label="Loading post" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-slate-400">
        <Newspaper className="w-16 h-16 opacity-20" />
        <div className="text-sm uppercase tracking-wider">Post not found.</div>
        <Link to="/community" className="text-gold font-bold text-sm hover:underline">← Back to Community</Link>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{`${post.title} — GamesTrade`}</title>
        <meta name="description" content={post.body.replace(/[#*\-]/g, "").slice(0, 160)} />
      </Helmet>

      {/* Full-width hero image */}
      {post.image ? (
        <div className="relative w-full h-[50vh] min-h-[300px] overflow-hidden -mt-16">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent" />
          {/* Title overlay on image */}
          <div className="absolute inset-x-0 bottom-0 site-container pb-10">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className={`inline-block text-[9px] uppercase tracking-[0.2em] border px-2.5 py-1 rounded-full font-bold mb-3 backdrop-blur-sm ${TAG_STYLES[post.tag] || TAG_STYLES.news}`}>
                {TAG_LABELS[post.tag] || post.tag}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white drop-shadow-lg max-w-3xl">
                {post.title}
              </h1>
            </motion.div>
          </div>
        </div>
      ) : (
        <div className="h-16" />
      )}

      <div className="site-container py-10">
        {/* Back link */}
        <div className="grid lg:grid-cols-[1fr_280px] gap-12 items-start">
          {/* Main content */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Title (no image case) */}
            {!post.image && (
              <>
                <span className={`inline-block text-[9px] uppercase tracking-[0.2em] border px-2.5 py-1 rounded-full font-bold mb-5 ${TAG_STYLES[post.tag] || TAG_STYLES.news}`}>
                  {TAG_LABELS[post.tag] || post.tag}
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-6">
                  {post.title}
                </h1>
              </>
            )}

            {/* Body — markdown rendered */}
            <div>{renderMarkdown(post.body)}</div>

            {/* Like */}
            <div className="flex items-center justify-between mt-12 pt-6 border-t border-gold/10">
              <button
                onClick={handleLike}
                disabled={liked}
                className={`flex items-center gap-2 text-sm font-bold transition px-5 py-2.5 rounded-full border ${
                  liked
                    ? "text-rose-400 border-rose-400/30 bg-rose-400/10"
                    : "text-slate-400 border-gold/20 hover:text-rose-400 hover:border-rose-400/30"
                }`}
              >
                <Heart className={`w-4 h-4 ${liked ? "fill-rose-400" : ""}`} />
                {liked ? "Liked" : "Like"} · {post.likes}
              </button>
              <Link
                to="/community"
                className="text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-gold transition"
              >
                ← All Posts
              </Link>
            </div>
          </motion.article>

          {/* Sidebar */}
          <aside className="hidden lg:block space-y-6 sticky top-24">
            <div className="bg-navy-700 rounded-2xl neon-border p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold mb-3 font-bold">About this post</div>
              <div className="space-y-3 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                  <span>{formatDateDisplay(post.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-slate-500 shrink-0" />
                  <span>{post.likes} {post.likes === 1 ? "like" : "likes"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] uppercase tracking-[0.2em] border px-2 py-0.5 rounded-full font-bold ${TAG_STYLES[post.tag] || TAG_STYLES.news}`}>
                    {TAG_LABELS[post.tag] || post.tag}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-navy-700 rounded-2xl neon-border p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold mb-3 font-bold">GamesTrade</div>
              <p className="text-xs text-slate-400 leading-relaxed">Sony Authorised dealer in Spectrum Mall, Sector 75, Noida. 25+ years serving Indian gamers.</p>
              <Link to="/shop" className="mt-3 block text-xs font-bold text-gold hover:underline uppercase tracking-wider">Shop Now →</Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
